<template>
  <div v-if="shouldShowShare">
    <div class="share-button" @click="handleShare">
      <img src="@/assets/share_icon.png" :alt="$t('actions.shareReport')" />
    </div>

    <div v-if="showDialog" class="share-dialog" @click="closeDialog">
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <span>{{ $t('share.dialogTitle') }}</span>
          <div class="close-btn" @click="closeDialog">×</div>
        </div>
        <div class="dialog-body">
          <img v-if="imageUrl" :src="imageUrl" :alt="$t('share.imageAlt')" />
          <div v-else class="loading">{{ $t('common.loading') }}</div>
          <div class="share-disclaimer">{{ shareDisclaimer }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { postImage } from '@/net/http';
import { SHARE_DISCLAIMER_TEXT } from '@/constants/compliance';

export default {
  name: 'ShareButton',
  data() {
    return {
      showDialog: false,
      imageUrl: '',
      shouldShowShare: false,
      shareDisclaimer: SHARE_DISCLAIMER_TEXT
    }
  },
  mounted() {
    // 检查当前路由，月度报告页面不显示分享按钮
    this.checkRoute();
  },
  watch: {
    '$route'() {
      this.checkRoute();
    }
  },
  methods: {
    checkRoute() {
      // 月度报告页面不显示分享按钮；上海小程序通过 hideShare=1 隐藏悬浮分享入口。
      if (!this.$route || !this.$route.path) {
        // 如果路由还未准备好，默认显示分享按钮
        this.shouldShowShare = true;
        return;
      }
      const currentPath = this.$route.path;
      const shouldHideByQuery = this.$route.query && this.$route.query.hideShare === '1';
      this.shouldShowShare = currentPath !== '/monthly-report' && !shouldHideByQuery;
    },
    async handleShare() {
      const urlParams = new URLSearchParams(window.location.search);
      const serialNumber = urlParams.get('packageNo');
      const platform = urlParams.get('platform');
      
      if (!serialNumber) {
        console.error('未找到报告标号 (packageNo)');
        return;
      }
      
      this.showDialog = true;
      this.imageUrl = '';
      
      try {
        const params = { serial_number: serialNumber };
        if (platform) params.platform = platform;
        const imageUrl = await postImage('/api/v1/wechat/miniprogram/share', params);
        this.imageUrl = await this.appendDisclaimerToImage(imageUrl);
      } catch (error) {
        console.error('分享接口调用失败:', error);
        alert(this.$t('share.loadImageFailed', { message: error.message }));
      }
    },
    closeDialog() {
      this.showDialog = false;
      if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.imageUrl);
      }
      this.imageUrl = '';
    },
    appendDisclaimerToImage(imageUrl) {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          const padding = Math.max(24, Math.round(image.width * 0.04));
          const fontSize = Math.max(24, Math.round(image.width * 0.032));
          const lineHeight = Math.round(fontSize * 1.55);
          const maxTextWidth = image.width - padding * 2;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          ctx.font = `${fontSize}px sans-serif`;
          const lines = this.wrapCanvasText(ctx, this.shareDisclaimer, maxTextWidth);
          const footerHeight = padding * 2 + lineHeight * lines.length;

          canvas.width = image.width;
          canvas.height = image.height + footerHeight;
          ctx.drawImage(image, 0, 0);
          ctx.fillStyle = '#f4f6f8';
          ctx.fillRect(0, image.height, canvas.width, footerHeight);
          ctx.font = `${fontSize}px sans-serif`;
          ctx.fillStyle = '#5f6b7a';
          ctx.textBaseline = 'top';
          lines.forEach((line, index) => {
            ctx.fillText(line, padding, image.height + padding + index * lineHeight);
          });

          canvas.toBlob((blob) => {
            if (blob) {
              URL.revokeObjectURL(imageUrl);
              resolve(URL.createObjectURL(blob));
              return;
            }
            resolve(imageUrl);
          }, 'image/png');
        };
        image.onerror = () => resolve(imageUrl);
        image.src = imageUrl;
      });
    },
    wrapCanvasText(ctx, text, maxWidth) {
      const lines = [];
      let line = '';
      Array.from(text).forEach((char) => {
        const nextLine = line + char;
        if (ctx.measureText(nextLine).width > maxWidth && line) {
          lines.push(line);
          line = char;
        } else {
          line = nextLine;
        }
      });
      if (line) {
        lines.push(line);
      }
      return lines;
    }
  }
}
</script>

<style scoped lang="scss">
.share-button {
  position: fixed;
  top: calc(80px + var(--report-navigation-offset, 0px));
  right: 0;
  cursor: pointer;
  z-index: 9999;
  transition: all 0.3s;

  &:active {
    transform: scale(0.95);
  }

  img {
    display: block;
    height: 52px;
    width: auto;
  }
}

.share-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.dialog-content {
  background: #fff;
  border-radius: 12px;
  width: 90vw;
  max-width: 500px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  font-size: 24px;
  font-weight: 500;

  @media screen and (max-width: 375px) {
    font-size: 22px;
  }
}

.close-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #999;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #333;
  }

  @media screen and (max-width: 375px) {
    width: 36px;
    height: 36px;
    font-size: 36px;
  }
}

.dialog-body {
  padding: 20px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 400px;

  img {
    width: 100%;
    height: auto;
    display: block;
  }

  .share-disclaimer {
    width: 100%;
    margin-top: 12px;
    padding: 10px 12px;
    box-sizing: border-box;
    border-radius: 10px;
    background: #f4f6f8;
    color: #6b7280;
    font-size: 12px;
    line-height: 1.6;
    text-align: left;
  }

  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 18px;

    &::before {
      content: '';
      width: 50px;
      height: 50px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #003cbf;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 16px;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
